import React, { Component, ErrorInfo, ReactNode } from 'react';
// Inline icon: the boundary wraps the public pages too, which must not pull in Fluent UI.
const ErrorCircleIcon = () => <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20Zm0 13.5a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5ZM12 6a1 1 0 0 0-1 .88V13a1 1 0 0 0 2 .12V7a1 1 0 0 0-1-1Z" /></svg>;
import '../styles/ErrorBoundary.scss';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
            errorInfo: null
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        this.setState({
            error,
            errorInfo
        });

        // Call custom error handler if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default error UI
            return (
                <div className="error-boundary">
                    <div className="error-boundary-content">
                        <div className="error-icon">
                            <ErrorCircleIcon />
                        </div>
                        <h1 className="error-title">Ops! Qualcosa è andato storto</h1>
                        <p className="error-message">
                            Si è verificato un errore imprevisto. Il nostro team è stato notificato.
                        </p>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="error-details">
                                <summary>Dettagli tecnici (solo in development)</summary>
                                <div className="error-stack">
                                    <strong>Errore:</strong>
                                    <pre>{this.state.error.toString()}</pre>

                                    {this.state.errorInfo && (
                                        <>
                                            <strong>Stack trace:</strong>
                                            <pre>{this.state.errorInfo.componentStack}</pre>
                                        </>
                                    )}
                                </div>
                            </details>
                        )}

                        <div className="error-actions">
                            <button
                                className="btn-primary"
                                onClick={this.handleReset}
                            >
                                Riprova
                            </button>
                            <button
                                className="btn-secondary"
                                onClick={() => window.location.href = '/dashboard'}
                            >
                                Torna alla Dashboard
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
